import React, { useState, useEffect } from 'react';

const API_BASE = "https://172.16.10.73:8080";

function LandingPage({ onAuthenticated }) {
  const [token, setToken] = useState('eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoiVEVTVF9VU0VSXzEyMzQ1NyIsImV4cCI6MTc2MjY3OTU0NiwiaWF0IjoxNzYyNjc1OTQ2LCJpc3MiOiJtYWluLXNlcnZpY2UiLCJhdWQiOiJhdXRoLXZlcmlmaWNhdGlvbi1zZXJ2aWNlIn0.EyVbGDc2hETMXoWdovIUI5dBl9JETzjqQulUm89J1Gk');
  const [status, setStatus] = useState({ type: 'info', message: '🔐 Enter your JWT token to begin' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkExistingSession();
  }, []);

  const checkExistingSession = async () => {
    const existingSession = localStorage.getItem('session_id');
    const storedToken = localStorage.getItem('jwt_token');
    
    if (existingSession && storedToken) {
      setStatus({ type: 'info', message: '🔄 Checking existing session...' });
      
      try {
        const response = await fetch(`${API_BASE}/api/session/validate`, {
          headers: { 'X-Session-Id': existingSession }
        });
        
        if (response.ok) {
          const data = await response.json();
          setStatus({ type: 'success', message: `✅ Session valid! Welcome back` });
          
          // Call parent callback instead of navigate
          if (onAuthenticated) {
            setTimeout(() => onAuthenticated(existingSession, data.user_id), 1500);
          }
          return;
        }
      } catch (err) {
        console.log('Session expired');
      }
    }
    
    if (storedToken) {
      setToken(storedToken);
      setStatus({ type: 'info', message: '🔑 Token found. Click authenticate.' });
    }
  };

  const authenticateWithToken = async (jwtToken) => {
    setLoading(true);
    setStatus({ type: 'info', message: '🔓 Authenticating...' });
    
    try {
      console.log('📡 Authenticating with token:', jwtToken.substring(0, 20) + '...');
      
      const response = await fetch(`${API_BASE}/api/authenticate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Auth failed:', errorText);
        throw new Error(`Auth failed (${response.status}): ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ Auth success:', data);
      
      localStorage.setItem('session_id', data.session_id);
      localStorage.setItem('user_id', data.user_id);
      localStorage.setItem('jwt_token', jwtToken);
      
      setStatus({ 
        type: 'success', 
        message: `✅ Session created!\nUser: ${data.user_id}` 
      });
      
      // Call parent callback instead of navigate
      if (onAuthenticated) {
        setTimeout(() => onAuthenticated(data.session_id, data.user_id), 2000);
      }
      
    } catch (err) {
      console.error('❌ Auth error:', err);
      setStatus({ type: 'error', message: `❌ ${err.message}` });
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!token.trim()) {
      setStatus({ type: 'error', message: '❌ Enter JWT token' });
      return;
    }
    authenticateWithToken(token.trim());
  };

  const handleClearSession = () => {
    localStorage.clear();
    setToken('');
    setStatus({ type: 'info', message: '🗑️ Cleared. Enter new token.' });
  };

  const getStatusClass = () => {
    switch (status.type) {
      case 'success': return 'bg-green-50 border-green-500 text-green-700';
      case 'error': return 'bg-red-50 border-red-500 text-red-700';
      default: return 'bg-blue-50 border-blue-500 text-blue-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
      <div className="fixed inset-0 opacity-10 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(16,185,129,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.3) 1px, transparent 1px)`,
          backgroundSize: '50px 50px',
        }} />
      </div>

      <div className="relative z-10 max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🔐</div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Identity Verification</h1>
          <p className="text-gray-600">Secure JWT Authentication</p>
        </div>

        <div className={`mb-6 p-4 rounded-lg border-2 ${getStatusClass()} whitespace-pre-line`}>
          <p className="text-sm font-medium">{status.message}</p>
        </div>

        {!loading && status.type !== 'success' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">JWT Token</label>
              <textarea
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-colors resize-none font-mono text-xs"
                rows={5}
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Sent via Authorization: Bearer header
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || !token.trim()}
              className="w-full py-3 bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold rounded-lg hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl"
            >
              {loading ? '🔄 Authenticating...' : '🚀 Authenticate & Start'}
            </button>

            {localStorage.getItem('session_id') && (
              <button
                type="button"
                onClick={handleClearSession}
                className="w-full py-2 text-sm text-gray-600 hover:text-red-600 transition-colors"
              >
                🗑️ Clear stored session
              </button>
            )}
          </form>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent mb-4" />
            <p className="text-sm text-gray-600">Authenticating...</p>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="space-y-2 text-xs text-gray-500">
            <p className="font-semibold text-gray-700">Flow:</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>JWT sent via <code className="bg-gray-100 px-1 rounded">Authorization</code> header</li>
              <li>Backend validates & creates Redis session</li>
              <li>Returns session_id & user_id</li>
              <li>Navigate to liveness → passport verification</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="fixed top-0 left-0 w-96 h-96 bg-green-500/5 rounded-full blur-3xl animate-pulse pointer-events-none" />
      <div className="fixed bottom-0 right-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl animate-pulse pointer-events-none" />
    </div>
  );
}

export default LandingPage;